import { Skeleton, Stack } from '@mui/material';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { ApiWorkflowDialog, DataGrid, DataGridLayout } from '@verantos-dev/shared-components';

import DetailsHeader from './DetailsHeader';
import { PrevalencePanel } from './PrevalencePanel';
import { useConceptGroupDetailsDialog } from '../../hooks/useConceptGroupDetailsDialog';

// TODO: cleanup with concept_group_prevalence_preview ff
const FF_PREVALENCE_PREVIEW = 'concept_group_prevalence_preview';

export interface ConceptGroupDetailsDialogProps {
  onClose: () => void;
  conceptGroupId: string;
  /**
   * Currently-pinned dataset release id (e.g. ``EXPORT.AMGEN_REGISTRY_2026Q1``)
   * used for the per-domain prevalence rollup. Optional — when absent the
   * prevalence panel surfaces a "pin a dataset" hint instead of fetching.
   * Only consumed when the ``concept_group_prevalence_preview`` FF is on.
   */
  datasetReleaseId?: string | null;
  /**
   * Human-readable name of the dataset release (the curated catalog
   * RELEASE_NAME) — preferred for display over the raw release id. When
   * absent the panel falls back to ``datasetReleaseId``.
   */
  datasetReleaseDisplayName?: string | null;
}

export const ConceptGroupDetailsDialog = ({
  onClose,
  conceptGroupId,
  datasetReleaseId = null,
  datasetReleaseDisplayName = null,
}: ConceptGroupDetailsDialogProps) => {
  const isPrevalencePreviewEnabled = useFeatureFlagSplit(FF_PREVALENCE_PREVIEW);
  const {
    conceptGroupDetails,
    columns,
    useGetConceptsData,
    sortModel,
    isConceptGroupDetailsLoading,
    exporting,
    handleExportAll,
  } = useConceptGroupDetailsDialog(conceptGroupId);

  return (
    <ApiWorkflowDialog
      id="concept-group-details-dialog"
      title={
        isConceptGroupDetailsLoading ? (
          <Skeleton variant="text" height={40} width={200} />
        ) : (
          conceptGroupDetails?.conceptGroupName
        )
      }
      displayButtons={false}
      size={'full'}
      onClose={onClose}
      isValidForSubmit={false}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    >
      <Stack direction="column" gap={2}>
        <DetailsHeader
          conceptGroupDetails={conceptGroupDetails}
          isConceptGroupDetailsLoading={isConceptGroupDetailsLoading}
          exporting={exporting}
          onExportAll={handleExportAll}
        />
        {isPrevalencePreviewEnabled && (
          <PrevalencePanel
            conceptGroupId={conceptGroupId}
            datasetReleaseId={datasetReleaseId}
            datasetReleaseDisplayName={datasetReleaseDisplayName}
            domainsHint={conceptGroupDetails?.domains}
          />
        )}
        <DataGridLayout id="concept-group-details-datagrid-layout" sx={{ maxHeight: '500px' }}>
          <DataGrid
            id="concept-group-details-grid"
            mode="server"
            disableSearchParamSync
            getRowId={(row) => row.conceptId}
            columns={columns}
            useFetchDataGridData={useGetConceptsData}
            pageSizeOptions={[10, 25, 50, 100]}
            initialPageSize={100}
            pagination
            getRowCount={(data) => data.pagination.total}
            disableColumnPinning
            loadOnFetch
            disableMultipleColumnsFiltering
            initialState={{
              sorting: {
                sortModel,
              },
            }}
            slotProps={{
              columnHeaders: {
                style: {
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  whiteSpace: 'nowrap',
                },
              },
            }}
          />
        </DataGridLayout>
      </Stack>
    </ApiWorkflowDialog>
  );
};
